import type { IconName } from './components/Icon';
import type { SearchHit, TaskStatus } from './api';
import { localDay } from './timezones';
import { readableDay, readableRange } from './dateLabels';

/**
 * What a status and a priority look like, in one place.
 *
 * Before this the words were written out wherever they were needed — `'blocked'` as a literal in
 * one component, `PRIORITY_LABELS` in another, a `PACE_TONE` map in a third — so the same idea had
 * three appearances and no way to change them together.
 *
 * **Shape carries the meaning; the tone only reinforces it.** That is not a style preference, it
 * is arithmetic. Colours on goals (five) plus projects (five) plus statuses (six) plus priorities
 * (four) is twenty distinguishable hues, and an eight-colour set already collapses to ΔE 0.3 under
 * simulated deuteranopia where five holds at 9.8. Twenty is not available at any price. So statuses
 * and priorities spend nothing from the palette — they are glyphs, tinted with the `--ok`,
 * `--warn` and `--bad` tones the app already had — and the five colours stay reserved for saying
 * *which container this belongs to*, which is the question colour is genuinely good at.
 *
 * It also satisfies SC 1.4.1 for free: a blocked hexagon and a dropped struck-circle differ as
 * outlines, so nothing depends on telling red from grey.
 */

export interface StatusLook {
  label: string;
  icon: IconName;
  /** A class suffix, resolved to a token in `styles.css` — never a hex here. */
  tone: 'muted' | 'fg' | 'accent' | 'ok' | 'bad';
}

/** `blocked` is derived rather than stored, and is included because it is displayed like one. */
export type DisplayStatus = TaskStatus | 'blocked';

/**
 * The labels are the same words `statusWord` in `packages/core/src/domain/statusWords.ts` writes
 * into the thread's "Status changed from … to …." entry, capitalised for a pill. The web cannot
 * import core, so `test/unit/statusWords.spec.ts` holds the two in agreement — rename one and that
 * spec fails until the other follows.
 */
export const STATUS_LOOK: Record<DisplayStatus, StatusLook> = {
  inbox: { label: 'Inbox', icon: 'inbox', tone: 'muted' },
  next: { label: 'Next', icon: 'next', tone: 'fg' },
  in_progress: { label: 'In progress', icon: 'start', tone: 'accent' },
  parked: { label: 'Parked', icon: 'park', tone: 'muted' },
  done: { label: 'Done', icon: 'done', tone: 'ok' },
  dropped: { label: 'Dropped', icon: 'drop', tone: 'muted' },
  blocked: { label: 'Blocked', icon: 'blocked', tone: 'bad' },
};

export interface StatusVerb {
  /** The label on a control that moves a task to this status. */
  verb: string;
  /** The same act as a sentence, for the key panel — "x — Complete it". */
  sentence: string;
  /** The state it lands in, for a readback of what just happened — "Started". */
  past: string;
}

/**
 * What each transition is *called*, next to what each state is called.
 *
 * `STATUS_LOOK` above names the five states; this names the five acts that reach them, and the
 * pair is the whole point. Before this, one act had four names — `Done` on the selection bar and
 * in the row menu, `Complete` on both detail surfaces, `Mark it done` in the key panel, `Finish`
 * on the status box's own label — and all four call `completeTask`. A reader who cannot tell
 * whether two words are two acts has to press one to find out.
 *
 * **The bar's five buttons are one part of speech now.** They read `To inbox · To next · Start ·
 * Complete · Drop`, where they read `Start | Next | Inbox | Done | Drop` — two verbs, two bare
 * nouns and an adjective, which is why "Next" could be read as *advance this task* rather than
 * *move it to Next*. `inbox` and `next` get a destination phrase rather than a verb because
 * English has no imperative for them that anybody would guess; the point is that every button
 * says what it does to the task, not that every one is a single word.
 *
 * **`past` is not decoration.** The list's readback used to be built by lower-casing whatever the
 * button said — `Moved to ${label}` — so pressing `3` announced "Moved to start" and `#`
 * announced "Moved to drop", naming the control instead of the state. A verb and a state are two
 * different words for a reason; keeping both here is what stops one being spent as the other.
 *
 * **`verb` and `STATUS_LOOK.label` differ for `done` on purpose**: the act is `Complete`, the
 * state is `Done`, and `Completed` is the same verb in the past tense so the disabled button and
 * the readback both stay in the one family. Naming the act "Done" would put an adjective on a
 * button; naming the state "Complete" would put a verb on a pill.
 */
export const STATUS_VERB: Record<TaskStatus, StatusVerb> = {
  inbox: { verb: 'To inbox', sentence: 'Move it to the inbox', past: 'Moved to inbox' },
  next: { verb: 'To next', sentence: 'Move it to next', past: 'Moved to next' },
  in_progress: { verb: 'Start', sentence: 'Start it', past: 'Started' },
  /* `Park` and `Drop` are two decisions and the words have to keep them apart: dropping says *I
     decided against this*, parking says *I have not decided and I am not pretending otherwise*.
     "Shelve" and "Snooze" were the other two candidates — the first is a synonym nothing else in
     the app uses, and the second promises a wake-up time this state deliberately does not have. */
  parked: { verb: 'Park', sentence: 'Park it for now', past: 'Parked' },
  done: { verb: 'Complete', sentence: 'Complete it', past: 'Completed' },
  dropped: { verb: 'Drop', sentence: 'Drop it', past: 'Dropped' },
};

/**
 * Which key moves a task to which status, in one place.
 *
 * It lived in `TaskCommands.tsx` as part of `COMMANDS`, beside an icon and a label, and `keys.ts`
 * restated the same bindings by hand — so `#` was bound and worked and was not in the registry,
 * which is the one thing that file's own header says must never be true: *a bound key that is not
 * listed is a key nobody will find.* One table, read by the command bar, the list's key handler and
 * the help, is what makes "listed" follow from "bound" rather than from somebody remembering.
 *
 * Here rather than in the component because `keys.ts` is a registry and must not import JSX to
 * read it — `COMMANDS` carries an `IconName` and the component composes this with that. This file
 * is what `keys.ts` already imports for `STATUS_VERB`, and the key and the verb belong together:
 * both are what the app *calls* a transition.
 *
 * Order is the command bar's reading order, which is also the order the help prints.
 */
export const STATUS_KEYS: { status: TaskStatus; key: string }[] = [
  { status: 'in_progress', key: '3' },
  { status: 'next', key: '2' },
  { status: 'inbox', key: '1' },
  { status: 'done', key: 'x' },
  /* The one that was bound and unlisted. `Drop` is deliberately not `Park` — see `STATUS_VERB`. */
  { status: 'dropped', key: '#' },
];

/**
 * Zero renders nothing at all.
 *
 * Absence is the information: on the owner's accounts most tasks have no priority, so a "None"
 * badge on every row would be noise proportional to how little anybody uses the field.
 */
export const PRIORITY_LABELS = ['None', 'Low', 'Medium', 'High'] as const;

/** The status a row should *display*, which is not always the one stored. */
export function displayStatus(task: { status: TaskStatus; isBlocked: boolean }): DisplayStatus {
  // Blocked outranks the stored status: "next, but blocked by something" is one fact, and the
  // useful half of it is the blocker.
  /* `parked` joins the two endings here, and for the display reason rather than the settled one:
     a task you have put down is not one you need told is blocked. The blocker is news again the
     moment it comes back off the shelf. */
  if (
    task.isBlocked &&
    task.status !== 'done' &&
    task.status !== 'dropped' &&
    task.status !== 'parked'
  )
    return 'blocked';
  return task.status;
}

export interface DueLook {
  text: string;
  /** `bad` is a missed deadline. `warn` is one arriving. `quiet` is everything else. */
  tone: 'bad' | 'warn' | 'quiet';
  /*
    How late, in steps, for a missed deadline. Absent when nothing is late.

    Computed styles across 56 real tasks returned exactly two treatments: 25 rows at the same red
    and 8 grey. *2 days late* and *12 days late* were identical in colour, weight and size — so on
    a screen 43% red, red had stopped sorting anything. The number was already on the pill; only
    its prominence was flat.

    Three steps rather than a continuous ramp: a week is a different conversation from yesterday,
    and a month is a different one again, but "nine days" and "eleven days" are not.
  */
  lateness?: 'slipping' | 'late' | 'stale';
  /**
   * Set when the deadline is the parent's rather than this task's own.
   *
   * Provenance, exactly as colour has `from`. Without it a subtask goes red for a date that is
   * nowhere on it, and there is no way to trace the red back to the task that caused it.
   */
  from?: 'its parent';
}

/**
 * Whole calendar days from one ISO day to another, on the strings — noon to noon, so no zone and no
 * DST change enters it. The one copy: `habitFigure` counts a reading's age with it too.
 */
export const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);

/** A deadline already turned into the account's day — the words `dueLook` gives a `dueAt`. */
function deadlineLook(due: string, today: string): DueLook {
  const delta = daysBetween(today, due);
  if (delta < 0) {
    const days = -delta;
    return { text: `${days}d late`, tone: 'bad', lateness: latenessOf(days) };
  }
  if (delta === 0) return { text: 'due today', tone: 'warn' };
  if (delta === 1) return { text: 'due tomorrow', tone: 'warn' };
  return { text: `due ${readableDay(due)}`, tone: 'quiet' };
}

/**
 * What a task's date should say, and how loudly.
 *
 * One pill used to encode one state where the data has four, and two people asked for opposite
 * repairs. On Today, a task whose `scheduledDate` had passed wore a red "overdue" — while its
 * `dueAt` was null and the pill's own tooltip admitted "Was planned for 2026-07-20". Nothing was
 * late; the only offence was having once been planned, reported as failure on the first screen of
 * the day. On the Tasks list the opposite: `due 8/14` on a day when 8/14 was a week gone rendered
 * in exactly the same grey as `due 8/31`, so nothing in a 56-row list stood out.
 *
 * Both are the same defect. Red is reserved for a real `dueAt` in the past and says *how* late, so
 * one day and forty days differ; a stale plan is stated in neutral words as the fact it is; and a
 * deadline arriving is emphasised without being an alarm.
 *
 * `today` is passed rather than read, because Today's shortlist asks this question about the day
 * being *viewed*, which is not always the current one.
 *
 * `timezone` is the account's, and is the second half of the same question. `dueAt` is a stored
 * instant; which day it belongs to depends on the zone it is read in, and this used to read it in
 * the *browser's* — so a deadline of 22:30 UTC was "due today" to a laptop in London and "due
 * tomorrow" to the same account's phone in Warsaw, on the same account's data. Every caller gets
 * both from `useAccountDay()`; Today passes the viewed day beside the account's zone.
 */
export function dueLook(
  task: {
    dueAt?: string | null;
    scheduledDate?: string | null;
    parent?: { dueAt: string | null } | null;
  },
  today: string,
  timezone: string,
): DueLook | null {
  /*
    Its own deadline, or its parent's when it has none.

    Resolved here rather than written onto the child, so moving a parent's date moves every
    inheriting subtask at once and nothing goes stale. This is the one place every surface asks
    the question, which is what keeps them all answering it the same way.
  */
  const inherited = !task.dueAt && task.parent?.dueAt ? task.parent.dueAt : null;
  const at = task.dueAt ?? inherited;
  const from = inherited ? ({ from: 'its parent' } as const) : {};
  if (at) return { ...deadlineLook(localDay(new Date(at), timezone), today), ...from };
  /*
    No deadline, but planned for a day that has passed. A fact, not a verdict — and now a fact with
    an age on it.

    The neutral tone stays: a plan is not a promise, and one persona's whole complaint was that a
    passed plan wore red. But "quiet" was collapsing *how long ago* into nothing, so on an account
    with no deadlines at all — which is most accounts — `planned 20 Jul` and `planned yesterday`
    rendered identically, and six rows sitting one and two days past their plan looked exactly like
    work planned for next week. The grading is carried in weight, not in hue, so age is legible
    without the plan becoming an accusation.
  */
  if (task.scheduledDate && task.scheduledDate < today) {
    const days = -daysBetween(today, task.scheduledDate);
    return {
      text: `planned ${readableDay(task.scheduledDate)}`,
      tone: 'quiet',
      lateness: latenessOf(days),
    };
  }
  return null;
}

/**
 * The three steps, and where they sit.
 *
 * `3 / 14` bunched real data badly: on an account with 25 overdue tasks the classes came out
 * `is-late 22, is-slipping 3, is-stale 0` — the grading shipped, and 22 of 25 rows still looked
 * identical, because almost all overdue work is between three days and a fortnight over. Most of
 * the ramp was being spent on a range nobody's data occupies.
 *
 * `3 / 7` keeps the lower boundary, which was reasoned about and is fine, and moves only the upper
 * one, which was not: inside the week is late, past a week it has stopped being about the deadline
 * and started being about the task.
 *
 * Measured on that same account afterwards, its 25 overdue deadlines are `3,3,3,4×8,5×8,6,8,8,8,
 * 9,13` — so the steps come out 3 / 17 / 5 where they were 3 / 22 / 0. All three now fire and the
 * oldest work separates from the rest, which is what the reader is scanning for. Seventeen rows
 * still share the middle step, and no absolute boundary can fix that: two thirds of this account's
 * overdue work is four or five days over, and four days and five days really are the same
 * situation. Telling those apart is a job for sorting, which is filed separately.
 */
function latenessOf(days: number): 'slipping' | 'late' | 'stale' {
  return days <= 3 ? 'slipping' : days <= 7 ? 'late' : 'stale';
}

/**
 * The class for a `DueLook`, so every surface paints the same state the same way.
 *
 * The step is appended for a quiet pill too. That pill is a plan that has passed rather than a
 * missed deadline, so it keeps the neutral hue — but it still has an age, and dropping the step
 * here is what made "planned yesterday" and "planned three weeks ago" identical on an account
 * with no deadlines at all. See the `:not(.bad)` rules in `styles.css`.
 */
export function dueClass(look: DueLook): string {
  const lateness = look.lateness ? ` is-${look.lateness}` : '';
  return look.tone === 'quiet' ? `pill${lateness}` : `pill ${look.tone}${lateness}`;
}

/**
 * What the pill says on hover: which date it is grading, and where an inherited one came from.
 *
 * It used to say "No deadline set" on every planned pill — technically true and unhelpful, since
 * the pill is plainly showing a date. What the reader cannot see is *which* date, and the two are
 * graded on the same three steps but mean different things: a missed deadline is a broken promise,
 * a passed plan is only a plan that did not happen.
 */
export function dueTitle(look: DueLook): string | undefined {
  if (look.from) return `Deadline from ${look.from}`;
  if (look.tone === 'quiet' && look.lateness) return 'A day you planned it for, not a deadline';
  return undefined;
}

/**
 * What deleting this task will take with it.
 *
 * Deleting a parent has always cascaded to its subtasks, which was harmless while a subtask was a
 * line of text nothing pointed at. They are real, schedulable work now — and once projects ship,
 * work that may belong to someone else — so the confirmation has to name the count, the same
 * treatment goal deletion already gets.
 *
 * Written once because there are two confirm sites, and a comment at one of them already says it
 * is meant to match the other.
 */
export function deleteTaskPrompt(task: { title: string; subtasks?: { total: number } }): string {
  const n = task.subtasks?.total ?? 0;
  if (n === 0) return `Delete "${task.title}"?`;
  return `Delete "${task.title}" and its ${n} subtask${n === 1 ? '' : 's'}?`;
}

/**
 * The period a review covers, in the words the copy needs.
 *
 * Phase 9 added the week/month/quarter switcher and every string kept saying "week": a quarter
 * review titled its chart "EACH DAY THIS WEEK", labelled its pills "311 / 1840 this week", and
 * closed with "Next week". Two personas reported it independently, and the maximizer found the
 * sharper version — the same habit reading "72 / 130 this month" in one view and
 * "72 / 390 this week" in another, one of them simply wrong.
 *
 * The period is already on the payload. Every string that names a span takes it from here, so
 * there is one place to change when a fourth period arrives.
 */
export function periodWords(period: 'week' | 'month' | 'quarter') {
  return {
    /** "this week" / "this month" / "this quarter" — for a figure inside the period. */
    thisOne: `this ${period}`,
    /**
     * The chart's own title, naming the unit it actually plots.
     *
     * A quarter is drawn one column per *week* — ninety-two columns at 4.4px was a blank box with
     * a hairline in it — while the heading went on saying "Each day this quarter". The chart and
     * its heading have to agree about what a bar is.
     */
    eachDay: period === 'quarter' ? 'Each week this quarter' : `Each day this ${period}`,
    /** "Next week" — the closing prompt, which asks about the span after this one. */
    next: `Next ${period}`,
    /** "No goal moved this week." */
    noMovement: `No goal moved this ${period}.`,
    /** Sentence-case name, for a heading. */
    title: period[0]!.toUpperCase() + period.slice(1),
    /** The bare noun, for mid-sentence use. */
    period,
    /**
     * What the headline and the stat tile count: habits at their target *for this period*.
     *
     * The browser's copy of `habitsMetLabel` in `packages/core/src/domain/weekReview.ts`, for the
     * same reason `goalMoved` is duplicated below — the bundle does not import `@planner/core`.
     */
    habitsMet: `at their ${period} target`,
  };
}

/**
 * What one of a habit's own periods is called — "day" / "days".
 *
 * Lived as a private const in `HabitHistory.tsx` while the reflection card, counting the same
 * periods for the same habits, printed a bare `(0 of 2)` beside a bare `(0 of 6)`: two weeks and
 * six days in one sentence with neither unit written. One noun, one place, so a second surface
 * cannot arrive at a different word for it.
 *
 * Keyed loosely because the caller's `period` is a string off the wire; `week` is the fallback for
 * the same reason it is the column default.
 */
export const HABIT_PERIOD_NOUN: Record<string, { one: string; many: string }> = {
  day: { one: 'day', many: 'days' },
  week: { one: 'week', many: 'weeks' },
  month: { one: 'month', many: 'months' },
};

/**
 * "paused since 6 Sep" — the clause that replaces a verdict when the habit has been stood down.
 *
 * The date is `activeTo`, a fact about the habit rather than about the period, so it is composed
 * here rather than in `VERDICT_WORD`, which stays a vocabulary. It replaces the verdict word
 * outright: "too early to tell · 5 to go" over a habit switched off last week is three states
 * collapsed into one, and a bare "paused" leaves the reader asking since when — which is exactly
 * the question the pill's `title` attribute was answering to nobody.
 */
export function habitPausedSince(activeTo: string): string {
  return `paused since ${readableDay(activeTo)}`;
}

/**
 * "23 completed days" — the unit written out, and which 23 of them it counted.
 *
 * It returned "23 days", and naming the unit was only half the job. The number counts a habit's
 * *finished* periods and never the one on the card, and nothing in the sentence said so: two
 * personas read "Run (met 0 of 2 weeks)" on the review's rarely-met callout, saw Run's own row
 * reading "too early to tell" a scroll below, and took the pair for two verdicts on one question.
 * The adjective is what separates the stretches — the row is about the period on the card, the
 * ratio about the ones before it — and it is not optional, so there is no unqualified spelling of
 * this phrase left for a surface to reach for.
 *
 * *Finished* is all the adjective claims, and deliberately: `periodsConsidered` — what both
 * callers pass — is the finished **applicable** periods, so one that was paused, or one nobody
 * was there for, is out of the count as well. Which ones and why is a fact about that habit's
 * record rather than about this phrase, so it is qualified where it is known: `metSub` follows
 * this clause with "not counting 2 with nothing recorded".
 */
export function habitPeriodsCompleted(count: number, period: string): string {
  const noun = HABIT_PERIOD_NOUN[period] ?? HABIT_PERIOD_NOUN.week!;
  return `${count} completed ${count === 1 ? noun.one : noun.many}`;
}

/**
 * The other habit number on the same card, which is not the same number.
 *
 * The sparkline and the calendar count habits whose period *is* a day and which hit that day's
 * target — a weekly habit is never met by Tuesday's entry, and the server never counts one. Both
 * used to be called "habits met", so one card read "0 of 11 habits met" in its headline while its
 * own chart tooltips totalled six.
 */
export const DAILY_HABITS_MET_LABEL = 'daily habits met';

/**
 * Whether a goal moved inside a review period.
 *
 * Progress is computed from tasks, so a goal carried entirely by habits is 0% before and 0% after
 * — and "moved" has to mean more than a changed percentage or such a goal can never move. The
 * headline count in `buildWeekReview` uses exactly this rule; this is the browser's copy of it,
 * exported so the Goals section and the sentence above it cannot disagree again. They did: the
 * card read "2 goals moved" and "No goal moved this week" at once.
 */
export function goalMoved(goal: {
  percentBefore: number;
  percentAfter: number;
  habitActivity?: boolean;
}): boolean {
  return goal.percentAfter !== goal.percentBefore || goal.habitActivity === true;
}

/**
 * What the review prints where a moved goal's percentage would go, when `habitActivity` is the
 * only reason `goalMoved` is true.
 *
 * It said "its habits" — a sentence fragment in a column where every other entry is
 * "0% → 100% (+100)", so the one row that could not show a number showed no verb either, and a
 * reader could not tell whether it was a caption, a link or a failure to render. The word is
 * "moved" because that is the word the section and the headline count with, `goalMoved`: this
 * row's reason, in the vocabulary the rest of the card already uses.
 *
 * **Not "tracked by habits"**, which is the pill on the goal tree and the banner on the goal page
 * and is a different fact: that goal has *no task work at all* (`totalWeight === 0`). This row
 * only knows that a habit was logged and the percentage did not change, which is also true of a
 * goal with fifty tasks and a quiet week. Saying "tracked by habits" here would state the shape of
 * a goal from evidence about a week.
 *
 * Web-local, unlike `DAILY_HABITS_MET_LABEL` and `goalMoved`: `buildWeekReview` counts these goals
 * but never names one, so there is no core copy to mirror and nothing to drift from.
 */
export const MOVED_BY_HABITS_LABEL = 'moved by its habits';

/**
 * Which stretch of each period the comparison figures actually cover, as one caption.
 *
 * It printed the prior range alone — `against 2026-08-17 to 2026-08-17` under a header reading
 * `2026-08-24 → 2026-08-30`. Both are right: the week is one day old, so the previous week is read
 * one day in, and scoring seven days against one would be a verdict about the calendar. But the
 * only range on the screen with a shape to compare it against was the *whole* current period, so
 * what a reader saw was seven days against one. The arch persona's first reading was that the
 * previous end date was computed wrong, and it took three period switches to work out otherwise.
 *
 * So the elapsed part of *this* period is printed too, in the same separator, next to the stretch
 * of the previous one it was put against. Two ranges of the same shape, side by side.
 *
 * **Deliberately no sentence stating the rule.** "The same first N days of the previous month" is
 * the rule as it holds on most days and is false on the ones that matter: `priorAsOf` is clamped
 * at the previous period's own end, so a running March on its 30th is 30 elapsed days against
 * February's 28, and a finished March is 31 days against 28. A finished Q1 is 90 days against Q4's
 * 92. A caption claiming equal length would be a plausible, internally consistent falsehood in
 * exactly the cases a reader would want it — and the two ranges printed plainly let that reader
 * see the 30-against-28 instead of being told it is not there. Phase 86 ended on three commits of
 * the same shape, each replacing an enumeration of what a number covered with what the payload in
 * front of the reader could actually be checked against: `9793f7f`, `1550f03`, `3e7a4c8`.
 *
 * Null for a period nobody has reached. There is no elapsed part to name, and `soFarEnd` is
 * *before* `previous.start` there — `getWeekReview` for November 2026 read on 2026-09-10 returns
 * `previous: { start: '2026-10-01', end: '2026-10-31', soFarEnd: '2026-08-10' }`, a range running
 * backwards across two months, because the elapsed-day count goes negative on a period in the
 * future. Reflection never shows it: `unscoreable` suppresses the whole comparison for a period
 * that has not started. This refuses in its own right so the caption cannot be reused into that
 * hole — but the payload field is still wrong on both REST and MCP, and that is its own defect
 * rather than something a caption can paper over.
 */
export function comparisonCaption(review: {
  start: string;
  end: string;
  /** Today in the **account's** zone — the day the current side's figures are cut at. */
  today: string;
  inProgress: boolean;
  notStarted?: boolean;
  previous: { start: string; end: string; soFarEnd?: string; soFarTime?: string };
}): string | null {
  if (review.notStarted) return null;
  const { previous } = review;
  /*
    A running period is counted to today and a finished one to its own end — the same clamp
    `getWeekReview` applies to `asOf`, said in the words the caption needs. `today` comes off the
    payload rather than from `new Date()`: every figure on this card is cut in the account's zone,
    and the browser's is not the account's.
  */
  if (!review.inProgress) {
    return `${readableRange(review.start, review.end)}, against ${readableRange(previous.start, previous.end)}`;
  }
  return (
    `${readableRange(review.start, review.today)} so far, ` +
    `against ${readableRange(previous.start, previous.soFarEnd ?? previous.end)}` +
    /* The clock the *task* figures were cut at on that last day, supplied by the server because
       the server chose it. Habit entries carry a date and no time, so this qualifies the tasks and
       points rows only — which is the scope it has always had here. */
    `${previous.soFarTime ? ` (to ${previous.soFarTime})` : ''}`
  );
}

/**
 * The pill beside a search result, in the words the task's own row uses.
 *
 * It printed the server's discriminator raw: *2026-08-18* on a task the list calls *63d late*,
 * beside results pilled *inbox* and *daily* — an ISO string in the one slot that otherwise holds
 * a word (`01a0809a-3b90`). The server still sends the day, because MCP and REST callers want the
 * machine form and `contextKind` to read it by; this is where it becomes a sentence. A deadline
 * gets exactly `dueLook`'s words; a plan gets `planned …` with the day spelled, `today` and
 * `tomorrow` included, since a search result is not judged the way a shortlist row is and a plan
 * in the future is as much an answer as one in the past.
 *
 * `today` is the account's (`useAccountDay()`), and the server sends a deadline as the day it
 * falls on in the account's zone, so the two are comparable as strings.
 */
export function searchContextText(
  hit: Pick<SearchHit, 'context' | 'contextKind'>,
  today: string,
): string {
  if (hit.contextKind === 'dueDate') return deadlineLook(hit.context, today).text;
  if (hit.contextKind === 'scheduledDate') {
    const delta = daysBetween(today, hit.context);
    if (delta === 0) return 'planned today';
    if (delta === 1) return 'planned tomorrow';
    return `planned ${readableDay(hit.context)}`;
  }
  return hit.context;
}

/**
 * The line under the headline: which clock made the pile late, and how bad the worst of it is.
 *
 * A function so its branches can be checked — two of them are states nobody reaches by clicking
 * around (`null` needs a server that counted late rows and had no worst one, `0` a deadline that
 * passed earlier the same day). Today's band puts the total in its headline and the Tasks summary
 * in its Late tile; this says the same total as its two halves, so a reader adding them up gets
 * the total back. Here beside `dueLook` because it is the same vocabulary — both surfaces say it.
 *
 * A half at zero is left out rather than printed as "0 missed deadlines": the zero is a fact about
 * the other half, and the band's height budget has no room for it. `null` and `0` stay different
 * answers for the reason `oldestLateDays` keeps them apart — "the oldest by 0 days" is not a
 * sentence, and a row owed this morning is nonetheless late.
 */
export function latePileSentence(
  byDeadline: number,
  byPlan: number,
  oldestDays: number | null,
): string {
  const halves = [
    byDeadline > 0 && `${byDeadline} missed ${byDeadline === 1 ? 'deadline' : 'deadlines'}`,
    byPlan > 0 && `${byPlan} missed ${byPlan === 1 ? 'plan' : 'plans'}`,
  ].filter(Boolean);
  const said = halves.join(' and ');
  if (oldestDays === null) return `${said}.`;
  if (oldestDays === 0) return `${said}, since today.`;
  const days = `${oldestDays} ${oldestDays === 1 ? 'day' : 'days'}`;
  /* With one row there is nothing for it to be the oldest *of*. */
  return byDeadline + byPlan === 1 ? `${said}, by ${days}.` : `${said}, the oldest by ${days}.`;
}
